import { addDays, format, parseISO } from 'date-fns';
import { formatBlisterCycle } from './blisterFormat';

/**
 * PRX-48 — every string the pack surfaces render in a queue, in one place, the
 * same contract as `blisterFormat.ts` next door. No component formats a pack
 * value itself, so a chip, a cell and the Patient 360 header can never drift.
 * Copy is spec section 7 verbatim.
 *
 * NO DATE IS EVER BUILT FROM A BARE 'YYYY-MM-DD'. `new Date('2026-09-21')`
 * parses as UTC midnight and reads as 20 Sep in every western timezone, so
 * every parse here goes through `parseISO(value + 'T00:00:00')`.
 *
 * THE RIDING CALCULATION MIRRORS THE BACKEND'S `ridingPackDate`
 * (`backend-v2/src/orders/utils/blister-dates.util.ts`) and must keep giving
 * the same answer, weekend rule included. It is duplicated here rather than
 * fetched because it is a render concern over data the row already carries —
 * spec 4d: no new server aggregation and no fourth builder. The two are kept
 * honest by identical worked examples in both spec files.
 */

export const PACK_CHIP_LABEL = 'Pack';
export const CHANGE_CHIP_LABEL = 'Change pending';
export const CHANGE_CHIP_TOOLTIP =
  "This patient's pack changed and the change has not been verified. " +
  'No pack can be sent until someone signs off on it.';

/** 'YYYY-MM-DD' to '21 Sep 2026'. Never `new Date(dateStr)`. */
function day(dateStr: string): string {
  return format(parseISO(`${dateStr}T00:00:00`), 'd MMM yyyy');
}

/** The group line: `Pack due 21 Sep 2026 · 8 medications`. */
export function formatPackDueLabel(packDate: string, medicationCount: number): string {
  const noun = medicationCount === 1 ? 'medication' : 'medications';
  return `Pack due ${day(packDate)} · ${medicationCount} ${noun}`;
}

/** The alongside cell: `Rides with pack 21 Sep 2026`, or the honest alternative. */
export function formatRidesWith(ridesWithPackDate: string | null): string {
  return ridesWithPackDate ? `Rides with pack ${day(ridesWithPackDate)}` : 'Ships on its own date';
}

/** The order-history chip's tooltip: `Blister pack for 21 Sep 2026 · every 4 weeks`. */
export function packChipTooltip(packDate: string, cycleDays: number | null): string {
  return `Blister pack for ${day(packDate)} · ${formatBlisterCycle(cycleDays)}`;
}

/** Saturday back one day, Sunday back two — the backend's `rollBackOffWeekend`. */
function rollBackOffWeekend(dateStr: string): string {
  const d = parseISO(`${dateStr}T00:00:00`);
  const weekday = d.getDay();
  if (weekday !== 0 && weekday !== 6) return dateStr;
  return format(addDays(d, weekday === 6 ? -1 : -2), 'yyyy-MM-dd');
}

/**
 * Which pack this alongside item travels with, or null when it ships alone.
 *
 * The nearest pack on or before the item's own due date, inside one cycle
 * window. NEVER a pack that comes after it: a six-month injectable is not held
 * back for a later pack (spec 4c, decision 11.4).
 *
 * An unusable cycle returns null rather than throwing — a queue cell must not
 * crash a grid, and an unusable cycle is reported where it can be fixed.
 */
export function ridingPackDateForItem(
  itemDueDate: string,
  nextPackDate: string,
  cycleDays: number,
): string | null {
  if (!Number.isInteger(cycleDays) || cycleDays <= 0) return null;
  const count = Math.ceil(180 / cycleDays) + 1;
  const windowStart = format(
    addDays(parseISO(`${itemDueDate}T00:00:00`), -(cycleDays - 1)),
    'yyyy-MM-dd',
  );
  let best: string | null = null;
  let cursor = nextPackDate;
  for (let i = 0; i < count; i++) {
    // ROLL, THEN STEP FROM THE ROLLED RUNG — the backend's `upcomingPackDates`
    // builds each rung as `advancePackDate` of the one before it, and that
    // function rolls its result. Stepping an unrolled cursor and rolling each
    // rung separately is NOT the same ladder: from 2026-09-02 on a 10-day cycle
    // the second rung is Saturday the 12th and rolls to Friday the 11th, so the
    // third is Monday the 21st here and Tuesday the 22nd there. Once a rung
    // lands on a weekend the two stay diverged, and the cell would name a
    // different pack from the Patient 360 header on the same screen.
    const candidate = rollBackOffWeekend(cursor);
    if (candidate <= itemDueDate && candidate >= windowStart) {
      if (best === null || candidate > best) best = candidate;
    }
    cursor = format(addDays(parseISO(`${candidate}T00:00:00`), cycleDays), 'yyyy-MM-dd');
  }
  return best;
}
